import { Link } from 'react-router-dom';
import { useState } from 'react';
import { useSEO } from '../../utils/seo';

export default function FormationsPage() {
  const [openModule, setOpenModule] = useState<number | null>(null);

  // SEO Configuration
  useSEO({
    title: 'Formations PowerPoint - Apprenez à Créer des Présentations Convaincantes | Izislide',
    description: 'Imagez vos idées : Formation PowerPoint complète. Apprenez le design PowerPoint, le storytelling et les techniques de présentation PPT. Devenez autonome sur vos présentations PowerPoint.',
    keywords: 'formation powerpoint, apprendre powerpoint, design présentation, storytelling powerpoint, formation ppt, cours powerpoint',
    canonical: '/formations',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Course',
      name: 'Formation PowerPoint - Design et Storytelling',
      description: 'Formation complète pour maîtriser la création de présentations PowerPoint professionnelles',
      provider: {
        '@type': 'Organization',
        name: 'Izislide',
        slogan: 'Imagez vos idées'
      },
      courseMode: 'blended',
      educationalLevel: 'Beginner to Advanced',
      hasCourseInstance: {
        '@type': 'CourseInstance',
        courseMode: 'blended',
        duration: 'P2D'
      },
      offers: {
        '@type': 'Offer',
        price: '1490',
        priceCurrency: 'EUR'
      }
    }
  });

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section 
        className="relative pt-32 pb-24 overflow-hidden bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(135deg, rgba(0,168,107,0.85) 0%, rgba(0,143,92,0.85) 100%), url('https://public.readdy.ai/ai/img_res/fd49af6cc733211d4d5d936488827314.jpg')`
        }}
      >
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              Apprenez à créer vous-même des présentations PowerPoint qui convainquent
            </h1>
            <p className="text-xl md:text-2xl mb-10 opacity-90">
              <strong>Imagez vos idées</strong> : Formation complète en design PowerPoint et storytelling de présentation
            </p>
            <a 
              href="#programme" 
              className="inline-block bg-white text-[#00A86B] px-10 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              Oui, je veux maîtriser PowerPoint
            </a>
          </div>
        </div>
      </section>

      {/* Pour qui Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
              Cette formation PowerPoint est faite pour vous si...
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white p-8 rounded-xl shadow-lg">
                <i className="ri-check-line text-3xl text-[#00A86B] mb-4"></i>
                <p className="text-lg text-gray-700">
                  Vous créez régulièrement des <strong>présentations PowerPoint</strong> et voulez gagner en impact
                </p>
              </div>
              <div className="bg-white p-8 rounded-xl shadow-lg">
                <i className="ri-check-line text-3xl text-[#00A86B] mb-4"></i>
                <p className="text-lg text-gray-700">
                  Vos <strong>présentations PPT</strong> actuelles manquent de clarté et de professionnalisme
                </p>
              </div>
              <div className="bg-white p-8 rounded-xl shadow-lg">
                <i className="ri-check-line text-3xl text-[#00A86B] mb-4"></i>
                <p className="text-lg text-gray-700">
                  Vous voulez devenir autonome sur le <strong>design PowerPoint</strong> sans être designer
                </p>
              </div>
              <div className="bg-white p-8 rounded-xl shadow-lg">
                <i className="ri-check-line text-3xl text-[#00A86B] mb-4"></i>
                <p className="text-lg text-gray-700">
                  Vous cherchez à structurer vos messages et maîtriser le storytelling de <strong>présentation PowerPoint</strong>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Programme Section */}
      <section id="programme" className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-12 text-center">
              Programme de la formation PowerPoint
            </h2>
            
            <div className="space-y-8">
              <div className="bg-[#C9D86A]/10 p-10 rounded-xl border-l-4 border-[#00A86B]">
                <div className="flex items-center mb-6">
                  <div className="w-16 h-16 bg-[#00A86B] rounded-full flex items-center justify-center text-white font-bold text-2xl mr-6">
                    1
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">Les fondamentaux du design PowerPoint</h3>
                </div>
                <ul className="space-y-3 ml-22 text-gray-700">
                  <li className="flex items-start">
                    <i className="ri-arrow-right-s-line text-xl text-[#00A86B] mr-2 mt-1"></i>
                    <span>Principes de design appliqués aux <strong>présentations PowerPoint</strong> : contraste, hiérarchie, espacement</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-arrow-right-s-line text-xl text-[#00A86B] mr-2 mt-1"></i>
                    <span>Typographie et couleurs dans PowerPoint : comment choisir et combiner</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-arrow-right-s-line text-xl text-[#00A86B] mr-2 mt-1"></i>
                    <span>Mise en page efficace de vos slides PowerPoint</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-arrow-right-s-line text-xl text-[#00A86B] mr-2 mt-1"></i>
                    <span>Visualisation de données dans PowerPoint : graphiques, tableaux, infographies</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-arrow-right-s-line text-xl text-[#00A86B] mr-2 mt-1"></i>
                    <span><strong>Imagez vos idées</strong> : transformer des concepts abstraits en visuels PowerPoint clairs</span>
                  </li>
                </ul>
              </div>

              <div className="bg-[#F7A348]/10 p-10 rounded-xl border-l-4 border-[#F7A348]">
                <div className="flex items-center mb-6">
                  <div className="w-16 h-16 bg-[#F7A348] rounded-full flex items-center justify-center text-white font-bold text-2xl mr-6">
                    2
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">Storytelling et structure de présentation PowerPoint</h3>
                </div>
                <ul className="space-y-3 ml-22 text-gray-700">
                  <li className="flex items-start">
                    <i className="ri-arrow-right-s-line text-xl text-[#F7A348] mr-2 mt-1"></i>
                    <span>Structurer votre message pour une <strong>présentation PowerPoint</strong> convaincante</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-arrow-right-s-line text-xl text-[#F7A348] mr-2 mt-1"></i>
                    <span>Les frameworks de storytelling appliqués aux <strong>présentations PPT</strong> (Pyramide de Minto, Hero's Journey...)</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-arrow-right-s-line text-xl text-[#F7A348] mr-2 mt-1"></i>
                    <span>Créer un fil conducteur captivant dans votre présentation PowerPoint</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-arrow-right-s-line text-xl text-[#F7A348] mr-2 mt-1"></i>
                    <span>Adapter votre <strong>présentation PowerPoint</strong> à votre audience</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-arrow-right-s-line text-xl text-[#F7A348] mr-2 mt-1"></i>
                    <span>Call-to-action efficaces dans vos slides PowerPoint</span>
                  </li>
                </ul>
              </div>

              <div className="bg-[#00A86B]/10 p-10 rounded-xl border-l-4 border-[#00A86B]">
                <div className="flex items-center mb-6">
                  <div className="w-16 h-16 bg-[#00A86B] rounded-full flex items-center justify-center text-white font-bold text-2xl mr-6">
                    3
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">Techniques avancées et optimisation PowerPoint</h3>
                </div>
                <ul className="space-y-3 ml-22 text-gray-700">
                  <li className="flex items-start">
                    <i className="ri-arrow-right-s-line text-xl text-[#00A86B] mr-2 mt-1"></i>
                    <span>Animations et transitions PowerPoint : quand et comment les utiliser</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-arrow-right-s-line text-xl text-[#00A86B] mr-2 mt-1"></i>
                    <span>Créer des templates PowerPoint réutilisables pour votre entreprise</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-arrow-right-s-line text-xl text-[#00A86B] mr-2 mt-1"></i>
                    <span>Workflow efficace : gagner du temps sur vos <strong>présentations PowerPoint</strong></span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-arrow-right-s-line text-xl text-[#00A86B] mr-2 mt-1"></i>
                    <span>Ressources et outils pour <strong>designer PowerPoint</strong> comme un pro</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-arrow-right-s-line text-xl text-[#00A86B] mr-2 mt-1"></i>
                    <span>Atelier pratique : refonte complète d'une <strong>présentation PowerPoint</strong> réelle</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Future Pacing */}
      <section className="py-20 bg-[#C9D86A]/10">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">
              Imaginez dans 2 mois...
            </h2>
            <div className="space-y-6 text-lg text-gray-700">
              <p className="flex items-start justify-center">
                <i className="ri-check-double-line text-2xl text-[#00A86B] mr-4 mt-1"></i>
                <span>Vous créez des <strong>présentations PowerPoint</strong> professionnelles en 2x moins de temps</span>
              </p>
              <p className="flex items-start justify-center">
                <i className="ri-check-double-line text-2xl text-[#00A86B] mr-4 mt-1"></i>
                <span>Vos collègues vous demandent conseil sur le <strong>design PowerPoint</strong></span>
              </p>
              <p className="flex items-start justify-center">
                <i className="ri-check-double-line text-2xl text-[#00A86B] mr-4 mt-1"></i>
                <span>Vos <strong>présentations PPT</strong> captivent votre audience du début à la fin</span>
              </p>
              <p className="flex items-start justify-center">
                <i className="ri-check-double-line text-2xl text-[#00A86B] mr-4 mt-1"></i>
                <span>Vous êtes autonome et confiant sur PowerPoint - <strong>Imagez vos idées</strong> devient votre réalité</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
            Ils ont suivi notre formation PowerPoint
          </h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <div className="bg-gray-50 p-8 rounded-xl">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-[#00A86B] rounded-full flex items-center justify-center text-white font-bold text-xl">
                  LM
                </div>
                <div className="ml-4">
                  <div className="font-bold text-gray-900">Laura Martinez</div>
                  <div className="text-sm text-gray-600">Chef de Projet</div>
                </div>
              </div>
              <p className="text-gray-700 italic mb-4">
                "Formation PowerPoint ultra concrète ! Je crée maintenant des <strong>présentations PPT</strong> en 2x moins de temps avec 3x plus d'impact."
              </p>
              <div className="flex text-[#F7A348]">
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
              </div>
            </div>

            <div className="bg-gray-50 p-8 rounded-xl">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-[#F7A348] rounded-full flex items-center justify-center text-white font-bold text-xl">
                  TD
                </div>
                <div className="ml-4">
                  <div className="font-bold text-gray-900">Thomas Dupont</div>
                  <div className="text-sm text-gray-600">Consultant</div>
                </div>
              </div>
              <p className="text-gray-700 italic mb-4">
                "Le module storytelling a transformé mes <strong>présentations PowerPoint</strong> clients. Mes taux de closing ont augmenté de 35% !"
              </p>
              <div className="flex text-[#F7A348]">
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
              </div>
            </div>

            <div className="bg-gray-50 p-8 rounded-xl">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-[#00A86B] rounded-full flex items-center justify-center text-white font-bold text-xl">
                  EB
                </div>
                <div className="ml-4">
                  <div className="font-bold text-gray-900">Emma Bernard</div>
                  <div className="text-sm text-gray-600">Responsable Marketing</div>
                </div>
              </div>
              <p className="text-gray-700 italic mb-4">
                "Enfin une formation PowerPoint pratique ! <strong>Imagez vos idées</strong>, c'est exactement ce que j'ai appris à faire."
              </p>
              <div className="flex text-[#F7A348]">
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
              Questions fréquentes sur la formation PowerPoint
            </h2>
            <div className="space-y-6">
              <details className="bg-white p-6 rounded-xl shadow-md cursor-pointer">
                <summary className="font-bold text-lg text-gray-900 cursor-pointer">
                  Faut-il déjà bien connaître PowerPoint pour suivre cette formation ?
                </summary>
                <p className="mt-4 text-gray-700">
                  Non ! La formation PowerPoint s'adresse à tous les niveaux. Nous partons des bases et allons jusqu'aux techniques avancées de <strong>design PowerPoint</strong>.
                </p>
              </details>

              <details className="bg-white p-6 rounded-xl shadow-md cursor-pointer">
                <summary className="font-bold text-lg text-gray-900 cursor-pointer">
                  Quel est le format de la formation PowerPoint ?
                </summary>
                <p className="mt-4 text-gray-700">
                  2 jours en présentiel ou distanciel, avec 70% de pratique. Vous repartez avec vos propres <strong>présentations PowerPoint</strong> retravaillées + templates personnalisés.
                </p>
              </details>

              <details className="bg-white p-6 rounded-xl shadow-md cursor-pointer">
                <summary className="font-bold text-lg text-gray-900 cursor-pointer">
                  La formation PowerPoint est-elle éligible au financement OPCO ?
                </summary>
                <p className="mt-4 text-gray-700">
                  Oui ! Izislide est organisme de formation certifié Qualiopi. Nous vous accompagnons dans vos démarches de financement pour la formation PowerPoint.
                </p>
              </details>

              <details className="bg-white p-6 rounded-xl shadow-md cursor-pointer">
                <summary className="font-bold text-lg text-gray-900 cursor-pointer">
                  Y a-t-il un suivi après la formation PowerPoint ?
                </summary>
                <p className="mt-4 text-gray-700">
                  Oui ! 3 mois d'accès à notre communauté privée + 2 sessions de Q&A en visio pour continuer à progresser sur vos <strong>présentations PowerPoint</strong>.
                </p>
              </details>
            </div>
          </div>
        </div>
      </section>

      {/* Guarantee */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center">
            <div className="w-24 h-24 bg-[#00A86B]/10 rounded-full flex items-center justify-center mx-auto mb-8">
              <i className="ri-shield-check-line text-5xl text-[#00A86B]"></i>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Garantie satisfaction 100%
            </h2>
            <p className="text-xl text-gray-700 mb-8">
              Si après le premier jour de formation PowerPoint vous n'êtes pas satisfait, nous vous remboursons intégralement. Sans condition.
            </p>
            <p className="text-lg text-gray-600">
              Nous sommes convaincus que cette formation PowerPoint va transformer votre façon de créer des <strong>présentations PPT</strong>. <strong>Imagez vos idées</strong> n'aura jamais été aussi simple.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-24 bg-gradient-to-r from-[#00A86B] to-[#008f5c]">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Prêt à maîtriser PowerPoint ?
            </h2>
            <p className="text-xl mb-4 opacity-90">
              Formation PowerPoint complète : Design + Storytelling
            </p>
            <p className="text-3xl font-bold mb-10">1490€ HT (éligible OPCO)</p>
            <a 
              href="/contact" 
              className="inline-block bg-white text-[#00A86B] px-10 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              Oui, je veux maîtriser mes présentations PowerPoint
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}